import { useCallback, useEffect, useState } from "react";
import { UserPlus, Copy, RefreshCw, Search, Users, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { euro } from "@/lib/dates";
import { PageHeader, Panel, Loader, Avatar, RoleBadge, Empty } from "@/components/common";
import { EmployeeDialog } from "@/components/EmployeeDialog";

const InviteCard = () => {
  const api = useApi();
  const { business, reload } = useMember();
  const copy = () => {
    navigator.clipboard?.writeText(business.invite_code);
    toast.success("Code gekopieerd");
  };
  const regen = async () => {
    try {
      await api.post("/business/invite-code");
      await reload();
      toast.success("Nieuwe code gemaakt. De oude code werkt niet meer.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel className="bg-[#0D5C46] text-white border-0 grain fade-up" data-testid="invite-code-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-100/80">Uitnodigingscode</p>
      <p className="font-mono-num text-3xl font-semibold tracking-[0.3em] mt-2" data-testid="invite-code-value">{business.invite_code}</p>
      <p className="text-sm text-emerald-50/80 mt-2">Medewerkers maken zelf een account aan en vullen deze code in.</p>
      <div className="flex gap-2 mt-4">
        <Button size="sm" variant="secondary" onClick={copy} data-testid="invite-code-copy-button"><Copy className="h-4 w-4 mr-1.5" />Kopiëren</Button>
        <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={regen} data-testid="invite-code-regenerate-button"><RefreshCw className="h-4 w-4 mr-1.5" />Nieuwe code</Button>
      </div>
    </Panel>
  );
};

export default function Employees() {
  const api = useApi();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = useCallback(() => api.get("/employees").then((r) => setList(r.data)), [api]);

  useEffect(() => {
    load();
  }, [load]);

  const { member, isOwner } = useMember();
  const rank = { owner: 3, manager: 2, employee: 1 };
  const canEdit = (e) => isOwner || rank[e.role] < rank[member.role];
  const shown = (list || []).filter((e) => `${e.name} ${e.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="employees-page">
      <PageHeader title="Medewerkers" subtitle={list ? `${list.filter((e) => e.active).length} actieve medewerkers` : ""}>
        <Button className="rounded-full" onClick={() => setDialog({ open: true, initial: null })} data-testid="add-employee-button"><UserPlus className="h-4 w-4 mr-2" />Medewerker toevoegen</Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <div className="min-w-0">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of e-mail" className="pl-9 h-11 bg-white" data-testid="employee-search-input" />
          </div>
          {!list ? <Loader /> : shown.length === 0 ? <Empty icon={Users} title={q ? "Geen medewerkers gevonden" : "Nog geen medewerkers"} text={q ? "Probeer een andere zoekterm." : "Voeg een medewerker toe of deel de uitnodigingscode hiernaast."} /> : (
            <div className="space-y-2" data-testid="employee-list">
              {shown.map((e) => (
                <Panel key={e.user_id} className={`p-4 flex items-center gap-3 ${!e.active ? "opacity-60" : ""}`} data-testid={`employee-row-${e.user_id}`}>
                  <Avatar name={e.name} size="h-11 w-11" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold truncate">{e.name}</p>
                      <RoleBadge role={e.role} />
                      {!e.active && <span className="text-xs font-semibold text-slate-500">Inactief</span>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{e.email}{e.phone ? ` · ${e.phone}` : ""}</p>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-semibold font-mono-num">{euro(e.hourly_rate)}<span className="text-xs text-slate-500 font-normal">/u</span></p>
                    <p className="text-xs text-slate-500">{e.contract_hours ? `${e.contract_hours} u/week` : "Oproep / flex"}</p>
                  </div>
                  {canEdit(e) && <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, initial: e })} data-testid={`edit-employee-${e.user_id}`} aria-label="Wijzigen"><Pencil className="h-4 w-4" /></Button>}
                </Panel>
              ))}
            </div>
          )}
        </div>
        <InviteCard />
      </div>

      <EmployeeDialog open={dialog.open} onOpenChange={(o) => setDialog((p) => ({ ...p, open: o }))} initial={dialog.initial} onSaved={load} />
    </div>
  );
}
